import React, { useEffect, useState, useMemo, useCallback, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RouteComponentProps } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faEnvelope,
  faSpinner,
  faCheck,
  faTrash,
  faExternalLinkAlt,
  faCopy,
  faPaperPlane,
  faUserPlus,
  faClock,
  faInfoCircle,
  faPencilAlt,
  faSyncAlt,
  faCommentAlt,
  faPlusCircle,
  faUserTie,
  faArchive,
  faUndo,
} from "@fortawesome/free-solid-svg-icons";

import { Routes } from "../../classes/Routes";
import Container from "../../components/UI/Container/Container";
import Spinner from "../../components/UI/Spinner/Spinner";
import Button from "../../components/UI/Button/Button";
import ConfirmDialog from "../../components/UI/ConfirmDialog/ConfirmDialog";
import ModalContext, { defaultModal } from "../../components/UI/Modal/ModalContext";
import LeadService, {
  ILead,
  ILeadNote,
  IBulkConvertResult,
  LeadStatus,
  LEAD_STATUS_LABELS,
  buildLeadTimeline,
} from "../../services/LeadService";
import { IAppState, IServiceState } from "../../interfaces/state";
import * as actions from "../../store/actions";
import classes from "./Leads.module.scss";

const CONTACT_METHOD_LABELS: Record<string, string> = {
  PHONE: "Puhelu",
  PUHELU: "Puhelu",
  EMAIL: "Sähköposti",
  VISIT: "Käynti",
  OTHER: "Muu",
};

type ViewMode = "table" | "pipeline";

interface IProps extends RouteComponentProps {}

const pipelineColumns: { status: LeadStatus; label: string; color: string }[] = [
  { status: LeadStatus.NOT_CONTACTED, label: "Ei kontaktoitu", color: "#64748b" },
  { status: LeadStatus.CONTACTED, label: "Kontaktoitu", color: "#2563eb" },
  { status: LeadStatus.NOT_INTERESTED, label: "Ei kiinnosta", color: "#dc2626" },
  { status: LeadStatus.CONTRACT, label: "Sopimus", color: "#16a34a" },
];

const Leads: React.FC<IProps> = ({ history }) => {
  const modal = useContext(ModalContext);
  const [leads, setLeads] = useState<ILead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("table");
  const [statusFilter, setStatusFilter] = useState<LeadStatus | "ALL">("ALL");
  const [searchQuery, setSearchQuery] = useState("");

  // Drawer state
  const [selectedLead, setSelectedLead] = useState<ILead | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activityFilter, setActivityFilter] = useState<"ALL" | "NOTES" | "CHANGES">("ALL");
  const [notes, setNotes] = useState<ILeadNote[]>([]);
  const [newNote, setNewNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteContent, setEditingNoteContent] = useState("");
  const [savingEditNote, setSavingEditNote] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [deletingLead, setDeletingLead] = useState(false);
  const [draggedLeadId, setDraggedLeadId] = useState<string | null>(null);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [selectedLeadIds, setSelectedLeadIds] = useState<string[]>([]);
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);

  // Bulk convert state
  const [selectedServiceToken, setSelectedServiceToken] = useState("");
  const [converting, setConverting] = useState(false);
  const [convertResult, setConvertResult] = useState<IBulkConvertResult | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  // Bulk delete state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Checkbox filters (B1)
  const [filterNewOnly, setFilterNewOnly] = useState<boolean>(false);
  const [filterOnMapOnly, setFilterOnMapOnly] = useState<boolean>(false);
  const [filterOyOnly, setFilterOyOnly] = useState<boolean>(false);
  const [filterTmiOnly, setFilterTmiOnly] = useState<boolean>(false);

  // Archive filter (Vaihtoehto 3: Aktiiviset, Arkisto, Kaikki)
  const [archiveFilter, setArchiveFilter] = useState<"ACTIVE" | "ARCHIVED" | "ALL">("ACTIVE");
  const [archiving, setArchiving] = useState<boolean>(false);

  // Pipeline outreach cadence filter (0/3, 1/3, 2/3, 3/3) for "Kontaktoitu" column
  const [pipelineContactFilter, setPipelineContactFilter] = useState<"ALL" | "0" | "1" | "2" | "3">("ALL");

  // Service filter (B2)
  const [serviceFilter, setServiceFilter] = useState<string>("");

  // CRM Tools state (C1, C2)
  const [copiedBcc, setCopiedBcc] = useState<boolean>(false);
  const [copiedDrawerEmail, setCopiedDrawerEmail] = useState<boolean>(false);

  const dispatch = useDispatch();
  const { services } = useSelector<IAppState, IServiceState>(state => state.services);

  useEffect(() => {
    if (!services) {
      dispatch(actions.listServices());
    }
  }, [dispatch, services]);

  // ---- Load leads ----
  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      const data = await LeadService.listLeads();
      setLeads(data);
      setSelectedLead((prev) => {
        if (!prev) return null;
        return data.find((l) => l.id === prev.id) || prev;
      });
    } catch (err: any) {
      setError("Liidien lataus epäonnistui: " + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  // Counts for filter badges
  const counts = useMemo(() => {
    return {
      newOnly: leads.filter((l) => !l.convertedToProvider).length,
      onMapOnly: leads.filter((l) => !!l.convertedToProvider).length,
      oyOnly: leads.filter((l) => !l.isToiminimi).length,
      tmiOnly: leads.filter((l) => !!l.isToiminimi).length,
      activeCount: leads.filter((l) => !l.archived).length,
      archivedCount: leads.filter((l) => !!l.archived).length,
      contact0: leads.filter((l) => ((l.salesContactCount ?? l.contactCount) || 0) === 0).length,
      contact1: leads.filter((l) => ((l.salesContactCount ?? l.contactCount) || 0) === 1).length,
      contact2: leads.filter((l) => ((l.salesContactCount ?? l.contactCount) || 0) === 2).length,
      contact3: leads.filter((l) => ((l.salesContactCount ?? l.contactCount) || 0) === 3).length,
    };
  }, [leads]);

  // ---- Filter & search ----
  const filteredLeads = useMemo(() => {
    let filtered = leads;
    if (archiveFilter === "ACTIVE") {
      filtered = filtered.filter((l) => !l.archived);
    } else if (archiveFilter === "ARCHIVED") {
      filtered = filtered.filter((l) => !!l.archived);
    }
    if (statusFilter !== "ALL") {
      filtered = filtered.filter((l) => l.status === statusFilter);
    }
    if (serviceFilter) {
      filtered = filtered.filter((l) => l.serviceToken === serviceFilter);
    }
    if (filterNewOnly && !filterOnMapOnly) {
      filtered = filtered.filter((l) => !l.convertedToProvider);
    } else if (!filterNewOnly && filterOnMapOnly) {
      filtered = filtered.filter((l) => !!l.convertedToProvider);
    }
    if (filterOyOnly && !filterTmiOnly) {
      filtered = filtered.filter((l) => !l.isToiminimi);
    } else if (!filterOyOnly && filterTmiOnly) {
      filtered = filtered.filter((l) => !!l.isToiminimi);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (l) =>
          (l.placeName || "").toLowerCase().includes(q) ||
          (l.ytjName || "").toLowerCase().includes(q) ||
          (l.city || "").toLowerCase().includes(q) ||
          (l.businessId || "").toLowerCase().includes(q)
      );
    }
    
    // Sort descending by created date and time (newest first)
    const sorted = [...filtered].sort((a, b) => {
      const timeA = a.created ? new Date(a.created).getTime() : 0;
      const timeB = b.created ? new Date(b.created).getTime() : 0;
      return timeB - timeA;
    });

    return sorted;
  }, [leads, archiveFilter, statusFilter, serviceFilter, filterNewOnly, filterOnMapOnly, filterOyOnly, filterTmiOnly, searchQuery]);

  // Active target leads for BCC based on selections (row checkboxes & filter toggles)
  const activeBccLeads = useMemo(() => {
    // 1. Base pool: if specific table rows are checked, use those. Otherwise use all filtered leads.
    const pool = selectedLeadIds.length > 0
      ? filteredLeads.filter((l) => selectedLeadIds.includes(l.id))
      : filteredLeads;

    // 2. Filter base pool by company form if filter toggles are active
    if (filterOyOnly && !filterTmiOnly) {
      return pool.filter((l) => !l.isToiminimi);
    }
    if (filterTmiOnly && !filterOyOnly) {
      return pool.filter((l) => !!l.isToiminimi);
    }
    // If specific rows are checked, respect the user's manual choices (all company forms in selection)
    if (selectedLeadIds.length > 0) {
      return pool;
    }
    // Default fallback when viewing all: default to Osakeyhtiöt for safe B2B email marketing
    return pool.filter((l) => !l.isToiminimi);
  }, [filteredLeads, selectedLeadIds, filterOyOnly, filterTmiOnly]);

  const bccEmails = useMemo(() => {
    return Array.from(
      new Set(
        activeBccLeads
          .filter((l) => l.email && l.email.includes("@"))
          .map((l) => l.email!.trim())
      )
    );
  }, [activeBccLeads]);

  const bccButtonLabel = useMemo(() => {
    const isSelected = selectedLeadIds.length > 0;
    const prefix = isSelected ? "Kopioi valitut" : "Kopioi";
    
    if (filterTmiOnly && !filterOyOnly) {
      return `${prefix} Tmi-sähköpostit BCC (${bccEmails.length})`;
    }
    if (filterOyOnly && !filterTmiOnly) {
      return `${prefix} Oy-sähköpostit BCC (${bccEmails.length})`;
    }
    if (isSelected) {
      return `Kopioi valitut sähköpostit BCC (${bccEmails.length})`;
    }
    return `Kopioi Oy-sähköpostit BCC (${bccEmails.length})`;
  }, [selectedLeadIds.length, filterTmiOnly, filterOyOnly, bccEmails.length]);

  const handleCopyBcc = useCallback(() => {
    if (bccEmails.length === 0) return;
    navigator.clipboard.writeText(bccEmails.join(", "));
    setCopiedBcc(true);
    setTimeout(() => setCopiedBcc(false), 2500);
  }, [bccEmails]);

  // ---- Lead selection handler (Shift-click range, Ctrl-click toggle, checkbox click) ----
  const handleToggleLeadSelection = useCallback((leadId: string, index?: number, isShift?: boolean) => {
    if (isShift && index !== undefined) {
      const fromIndex = lastSelectedIndex !== null ? lastSelectedIndex : 0;
      const start = Math.min(fromIndex, index);
      const end = Math.max(fromIndex, index);
      const rangeIds = filteredLeads.slice(start, end + 1).map((l) => l.id);
      setSelectedLeadIds((prev) => {
        const nextSet = new Set(prev);
        rangeIds.forEach((id) => nextSet.add(id));
        return Array.from(nextSet);
      });
      setLastSelectedIndex(index);
    } else {
      setSelectedLeadIds((prev) => {
        if (prev.includes(leadId)) {
          return prev.filter((id) => id !== leadId);
        } else {
          return [...prev, leadId];
        }
      });
      if (index !== undefined) {
        setLastSelectedIndex(index);
      }
    }
  }, [filteredLeads, lastSelectedIndex]);

  // ---- Drawer open ----
  const openDrawer = useCallback(async (lead: ILead) => {
    setSelectedLead(lead);
    setDrawerOpen(true);
    setActivityFilter("ALL");
    setEditingNoteId(null);
    setEmailSent(false);
    setNewNote("");
    setLoadingNotes(true);
    try {
      const full = await LeadService.getLead(lead.id);
      setSelectedLead(full);
      setNotes(full.notes || []);
    } catch (err) {
      console.error("Error fetching lead details:", err);
    } finally {
      setLoadingNotes(false);
    }
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    setSelectedLead(null);
    setNotes([]);
    setEditingNoteId(null);
  }, []);

  // Scroll lock background body when drawer is open
  useEffect(() => {
    if (drawerOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [drawerOpen]);

  // ---- Sales contact count update (0..3) ----
  const handleUpdateContactCount = useCallback(async (lead: ILead, newCount: number) => {
    const clamped = Math.max(0, Math.min(3, newCount));
    const newStatus = clamped > 0 && lead.status === LeadStatus.NOT_CONTACTED
      ? LeadStatus.CONTACTED
      : lead.status;

    // Optimistic UI update
    setLeads((prev) =>
      prev.map((l) => (l.id === lead.id ? { ...l, salesContactCount: clamped, contactCount: clamped, status: newStatus } : l))
    );
    if (selectedLead && selectedLead.id === lead.id) {
      setSelectedLead((prev) => (prev ? { ...prev, salesContactCount: clamped, contactCount: clamped, status: newStatus } : null));
    }

    try {
      const updated = await LeadService.updateLead(lead.id, {
        salesContactCount: clamped,
        contactCount: clamped,
        status: newStatus,
      });
      setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, ...updated } : l)));
      if (selectedLead && selectedLead.id === lead.id) {
        setSelectedLead((prev) => (prev ? { ...prev, ...updated } : null));
      }
    } catch (err) {
      console.error("Error updating sales contact count:", err);
      // Rollback
      setLeads((prev) =>
        prev.map((l) => (l.id === lead.id ? { ...l, salesContactCount: lead.salesContactCount, contactCount: lead.contactCount, status: lead.status } : l))
      );
      if (selectedLead && selectedLead.id === lead.id) {
        setSelectedLead((prev) => (prev ? { ...prev, salesContactCount: lead.salesContactCount, contactCount: lead.contactCount, status: lead.status } : null));
      }
    }
  }, [selectedLead]);

  // ---- Bulk archive / restore ----
  const handleBulkArchive = useCallback(async (shouldArchive: boolean) => {
    if (selectedLeadIds.length === 0) return;
    setArchiving(true);
    try {
      await LeadService.bulkArchiveLeads(selectedLeadIds, shouldArchive);
      setLeads((prev) =>
        prev.map((l) => (selectedLeadIds.includes(l.id) ? { ...l, archived: shouldArchive } : l))
      );
      if (selectedLead && selectedLeadIds.includes(selectedLead.id)) {
        setSelectedLead((prev) => (prev ? { ...prev, archived: shouldArchive } : null));
      }
      setSelectedLeadIds([]);
    } catch (err) {
      console.error("Bulk archive failed:", err);
      setError("Liidien arkistointi epäonnistui.");
    } finally {
      setArchiving(false);
    }
  }, [selectedLeadIds, selectedLead]);

  // ---- Toggle single lead archive ----
  const handleToggleArchiveLead = useCallback(async (lead: ILead) => {
    const nextArchived = !lead.archived;
    setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, archived: nextArchived } : l)));
    if (selectedLead && selectedLead.id === lead.id) {
      setSelectedLead((prev) => (prev ? { ...prev, archived: nextArchived } : null));
    }
    try {
      const updated = await LeadService.updateLead(lead.id, { archived: nextArchived });
      setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, ...updated } : l)));
      if (selectedLead && selectedLead.id === lead.id) {
        setSelectedLead((prev) => (prev ? { ...prev, ...updated } : null));
      }
    } catch (err) {
      console.error("Toggle archive failed:", err);
      setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, archived: lead.archived } : l)));
      if (selectedLead && selectedLead.id === lead.id) {
        setSelectedLead((prev) => (prev ? { ...prev, archived: lead.archived } : null));
      }
    }
  }, [selectedLead]);

  // ---- Mini-checkboxes renderer (3 square boxes) ----
  const renderContactStepBoxes = useCallback((lead: ILead) => {
    const count = (lead.salesContactCount ?? lead.contactCount) || 0;
    return (
      <div className={classes.ContactStepBoxes} onClick={(e) => e.stopPropagation()}>
        {[1, 2, 3].map((step) => {
          const isChecked = count >= step;
          return (
            <button
              key={step}
              type="button"
              className={`${classes.ContactStepBox} ${isChecked ? classes.ContactStepBoxChecked : ""}`}
              title={`Viesti ${step}/3: ${isChecked ? "Merkitty tehdyksi (klikkaa peruaksesi)" : "Ei lähetetty (klikkaa merkitäksesi)"}`}
              onClick={(e) => {
                e.stopPropagation();
                const nextCount = isChecked && count === step ? step - 1 : step;
                handleUpdateContactCount(lead, nextCount);
              }}
            >
              {isChecked ? "✓" : step}
            </button>
          );
        })}
      </div>
    );
  }, [handleUpdateContactCount]);

  // ---- Status update ----
  const handleStatusChange = useCallback(
    async (status: LeadStatus) => {
      if (!selectedLead) return;
      setUpdatingStatus(true);
      try {
        const updated = await LeadService.updateLead(selectedLead.id, { status });
        // Refresh full lead to get newly generated AuditTrail
        const full = await LeadService.getLead(selectedLead.id);
        setSelectedLead(full);
        setLeads((prev) => prev.map((l) => (l.id === updated.id ? { ...l, ...updated } : l)));
      } catch (err) {
        console.error("Error updating status:", err);
      } finally {
        setUpdatingStatus(false);
      }
    },
    [selectedLead]
  );

  // ---- Add note ----
  const handleAddNote = useCallback(async () => {
    if (!selectedLead || !newNote.trim()) return;
    setSavingNote(true);
    try {
      let serviceName: string | undefined = undefined;
      if (selectedLead.serviceToken && services) {
        const found = services.find((s) => s.serviceToken === selectedLead.serviceToken);
        if (found) serviceName = found.name;
      }
      const note = await LeadService.addLeadNote(
        selectedLead.id,
        newNote.trim(),
        selectedLead.serviceToken,
        serviceName
      );
      setNotes((prev) => [note, ...prev]);
      setNewNote("");
    } catch (err) {
      console.error("Error adding note:", err);
    } finally {
      setSavingNote(false);
    }
  }, [selectedLead, newNote, services]);

  // ---- Edit note ----
  const handleStartEditNote = useCallback((note: ILeadNote) => {
    setEditingNoteId(note.id);
    setEditingNoteContent(note.content);
  }, []);

  const handleCancelEditNote = useCallback(() => {
    setEditingNoteId(null);
    setEditingNoteContent("");
  }, []);

  const handleSaveEditNote = useCallback(async (noteId: string) => {
    if (!editingNoteContent.trim() || !selectedLead) return;
    setSavingEditNote(true);
    try {
      let serviceName: string | undefined = undefined;
      if (selectedLead.serviceToken && services) {
        const found = services.find((s) => s.serviceToken === selectedLead.serviceToken);
        if (found) serviceName = found.name;
      }
      const updated = await LeadService.updateLeadNote(
        noteId,
        editingNoteContent.trim(),
        selectedLead.serviceToken,
        serviceName
      );
      setNotes((prev) => prev.map((n) => (n.id === noteId ? updated : n)));
      setEditingNoteId(null);
      setEditingNoteContent("");
    } catch (err) {
      console.error("Error updating note:", err);
    } finally {
      setSavingEditNote(false);
    }
  }, [editingNoteContent, selectedLead, services]);

  // ---- Delete note ----
  const handleDeleteNote = useCallback((noteId: string) => {
    modal.setModal({
      ...defaultModal,
      isOpen: true,
      title: "Poista muistiinpano",
      content: (
        <ConfirmDialog
          body="Haluatko varmasti poistaa tämän muistiinpanon?"
          onConfirm={async () => {
            modal.setModal(defaultModal);
            try {
              await LeadService.deleteLeadNote(noteId);
              setNotes((prev) => prev.filter((n) => n.id !== noteId));
            } catch (err) {
              console.error("Error deleting note:", err);
            }
          }}
          onCancel={() => modal.setModal(defaultModal)}
        />
      ),
    });
  }, [modal]);

  // ---- Send email ----
  const handleSendEmail = useCallback(async () => {
    if (!selectedLead) return;
    setSendingEmail(true);
    try {
      const result = await LeadService.sendEmail(selectedLead.id);
      if (result.success) {
        setEmailSent(true);
        // Refresh full lead to get emailSentAt & audit trails
        const updated = await LeadService.getLead(selectedLead.id);
        setSelectedLead(updated);
        setLeads((prev) => prev.map((l) => (l.id === updated.id ? { ...l, ...updated } : l)));
      }
    } catch (err) {
      console.error("Error sending email:", err);
    } finally {
      setSendingEmail(false);
    }
  }, [selectedLead]);

  // ---- Delete lead ----
  const handleDelete = useCallback(async () => {
    if (!selectedLead) return;
    if (!window.confirm("Haluatko varmasti poistaa tämän liidin?")) return;
    setDeletingLead(true);
    try {
      await LeadService.deleteLead(selectedLead.id);
      setLeads((prev) => prev.filter((l) => l.id !== selectedLead.id));
      closeDrawer();
    } catch (err) {
      console.error("Error deleting lead:", err);
    } finally {
      setDeletingLead(false);
    }
  }, [selectedLead, closeDrawer]);

  // ---- Copy email to clipboard ----
  const handleCopyEmail = useCallback(() => {
    if (!selectedLead?.generatedEmailBody) return;
    const text = `${selectedLead.generatedEmailSubject || ""}\n\n${selectedLead.generatedEmailBody}`;
    navigator.clipboard.writeText(text);
  }, [selectedLead]);

  // ---- Timeline & Activity helpers ----
  const timelineItems = useMemo(() => {
    if (!selectedLead) return [];
    return buildLeadTimeline({ ...selectedLead, notes });
  }, [selectedLead, notes]);

  const filteredTimelineItems = useMemo(() => {
    if (activityFilter === "NOTES") {
      return timelineItems.filter((i) => i.type === "NOTE");
    }
    if (activityFilter === "CHANGES") {
      return timelineItems.filter((i) => i.type !== "NOTE");
    }
    return timelineItems;
  }, [timelineItems, activityFilter]);

  // Relative time helper
  const timeAgo = (dateStr?: string) => {
    if (!dateStr) return "";
    const diffMs = Date.now() - new Date(dateStr).getTime();
    const diffSec = Math.floor(diffMs / 1000);
    if (diffSec < 60) return "Juuri nyt";
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin} min sitten`;
    const diffHours = Math.floor(diffMin / 60);
    if (diffHours < 24) return `${diffHours} t sitten`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return "Eilen";
    if (diffDays < 7) return `${diffDays} pv sitten`;
    return formatDate(dateStr);
  };

  // Initials helper
  const getInitials = (name?: string) => {
    if (!name) return "?";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // ---- Status badge helper ----
  const statusBadgeClass = (status: LeadStatus) => {
    switch (status) {
      case LeadStatus.NOT_CONTACTED: return classes.StatusNotContacted;
      case LeadStatus.CONTACTED: return classes.StatusContacted;
      case LeadStatus.NOT_INTERESTED: return classes.StatusNotInterested;
      case LeadStatus.CONTRACT: return classes.StatusContract;
      default: return classes.StatusNotContacted;
    }
  };

  // ---- Format date ----
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("fi-FI");
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleString("fi-FI");
    } catch {
      return dateStr;
    }
  };

  // ---- Pipeline helpers ----

  const leadsByStatus = useMemo(() => {
    const map: Record<string, ILead[]> = {};
    for (const col of pipelineColumns) {
      let list = filteredLeads.filter((l) => l.status === col.status);
      if (col.status === LeadStatus.CONTACTED && pipelineContactFilter !== "ALL") {
        const target = parseInt(pipelineContactFilter, 10);
        list = list.filter((l) => ((l.salesContactCount ?? l.contactCount) || 0) === target);
      }
      map[col.status] = list;
    }
    return map;
  }, [filteredLeads, pipelineContactFilter]);

  // Search match helper for pipeline highlight
  const isSearchMatch = useCallback((lead: ILead) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (lead.placeName || "").toLowerCase().includes(q) ||
      (lead.ytjName || "").toLowerCase().includes(q) ||
      (lead.city || "").toLowerCase().includes(q) ||
      (lead.businessId || "").toLowerCase().includes(q)
    );
  }, [searchQuery]);

  if (loading) {
    return (
      <Container>
        <Spinner />
      </Container>
    );
  }

  return (
    <Container>
      {/* Header */}
      <div className={classes.LeadsHeader}>
        <div>
          <h2>Liidit</h2>
          <p className={classes.Sub}>{leads.length} liidiä yhteensä</p>
        </div>
        <div className={classes.Actions}>
          <select
            value={selectedServiceToken}
            onChange={(e) => setSelectedServiceToken(e.target.value)}
            style={{ padding: "0.5rem", borderRadius: "6px", border: "1px solid #d1d5db", marginRight: "0.5rem" }}
          >
            <option value="">Valitse palvelu...</option>
            {(services || []).filter(s => !s.archived && s.serviceToken).map((svc) => (
              <option key={svc.id} value={svc.serviceToken}>{svc.name || svc.serviceToken}</option>
            ))}
          </select>
          <Button
            onClick={() => setShowConfirm(true)}
            disabled={!selectedServiceToken || converting || selectedLeadIds.length === 0}
          >
            <FontAwesomeIcon icon={faUserPlus} style={{ marginRight: "0.4rem" }} />
            Tee valituista ({selectedLeadIds.length}) palveluntarjoajia
          </Button>
          <button
            type="button"
            className={classes.ArchiveBtn}
            onClick={() => handleBulkArchive(archiveFilter !== "ARCHIVED")}
            disabled={selectedLeadIds.length === 0 || archiving}
            style={{ marginRight: "0.5rem", marginLeft: "0.5rem" }}
            title={selectedLeadIds.length === 0 ? "Valitse vähintään yksi liidi" : (archiveFilter === "ARCHIVED" ? "Palauta valitut liidit aktiivisiksi" : "Arkistoi valitut liidit")}
          >
            {archiving ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <FontAwesomeIcon icon={archiveFilter === "ARCHIVED" ? faUndo : faArchive} />
            )}
            <span>
              {archiveFilter === "ARCHIVED"
                ? `Palauta aktiivisiksi ${selectedLeadIds.length > 0 ? `(${selectedLeadIds.length})` : ""}`
                : `Arkistoi valitut ${selectedLeadIds.length > 0 ? `(${selectedLeadIds.length})` : ""}`}
            </span>
          </button>
          <button
            type="button"
            className={classes.DeleteBtn}
            onClick={() => setShowDeleteConfirm(true)}
            disabled={selectedLeadIds.length === 0 || deleting}
            style={{ marginRight: "0.5rem" }}
            title={selectedLeadIds.length === 0 ? "Valitse vähintään yksi liidi poistettavaksi" : "Poista valitut liidit näkymästä"}
          >
            <FontAwesomeIcon icon={faTrash} />
            <span>Poista liidit näkymästä {selectedLeadIds.length > 0 ? `(${selectedLeadIds.length})` : ""}</span>
          </button>
          <Button onClick={() => history.push("/onboarding")}>+ Uusi liidi</Button>
        </div>
      </div>

      {/* View mode tabs & Archive tabs */}
      <div className={classes.HeaderRowControls}>
        <div className={classes.TabRow} style={{ marginBottom: 0 }}>
          <button
            className={`${classes.Tab} ${viewMode === "table" ? classes.TabActive : ""}`}
            onClick={() => setViewMode("table")}
          >
            Taulukko
          </button>
          <button
            className={`${classes.Tab} ${viewMode === "pipeline" ? classes.TabActive : ""}`}
            onClick={() => setViewMode("pipeline")}
          >
            Myyntiputki
          </button>
        </div>

        <div className={classes.ArchiveFilterTabs}>
          <button
            className={`${classes.ArchiveTab} ${archiveFilter === "ACTIVE" ? classes.ArchiveTabActive : ""}`}
            onClick={() => setArchiveFilter("ACTIVE")}
            title="Näytä vain aktiiviset liidit (oletus)"
          >
            Aktiiviset ({counts.activeCount})
          </button>
          <button
            className={`${classes.ArchiveTab} ${archiveFilter === "ARCHIVED" ? classes.ArchiveTabActive : ""}`}
            onClick={() => setArchiveFilter("ARCHIVED")}
            title="Näytä arkistoidut liidit"
          >
            Arkisto ({counts.archivedCount})
          </button>
          <button
            className={`${classes.ArchiveTab} ${archiveFilter === "ALL" ? classes.ArchiveTabActive : ""}`}
            onClick={() => setArchiveFilter("ALL")}
            title="Näytä kaikki liidit"
          >
            Kaikki ({leads.length})
          </button>
        </div>
      </div>

      {/* Enhanced Filters & Tools */}
      <div className={classes.FilterControlsWrapper}>
        <div className={classes.FilterRowPrimary}>
          <button
            className={`${classes.FilterBtn} ${statusFilter === "ALL" ? classes.FilterBtnActive : ""}`}
            onClick={() => setStatusFilter("ALL")}
          >
            Kaikki ({leads.length})
          </button>
          {Object.entries(LEAD_STATUS_LABELS).map(([key, label]) => {
            const count = leads.filter((l) => l.status === key).length;
            return (
              <button
                key={key}
                className={`${classes.FilterBtn} ${statusFilter === key ? classes.FilterBtnActive : ""}`}
                onClick={() => setStatusFilter(key as LeadStatus)}
              >
                {label} ({count})
              </button>
            );
          })}
          <input
            className={classes.SearchInput}
            type="text"
            placeholder="Hae nimellä, kaupungilla, Y-tunnuksella..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className={classes.FilterRowSecondary}>
          {/* Checkbox filters (B1) */}
          <div className={classes.CheckboxFiltersGroup}>
            <label className={classes.CheckboxLabel}>
              <input
                type="checkbox"
                checked={filterNewOnly}
                onChange={(e) => setFilterNewOnly(e.target.checked)}
              />
              <span>Uudet (ei kartalla)</span>
              <span className={classes.CheckboxBadgeCount}>{counts.newOnly}</span>
            </label>

            <label className={classes.CheckboxLabel}>
              <input
                type="checkbox"
                checked={filterOnMapOnly}
                onChange={(e) => setFilterOnMapOnly(e.target.checked)}
              />
              <span>Kartalla (palveluntarjoaja)</span>
              <span className={classes.CheckboxBadgeCount}>{counts.onMapOnly}</span>
            </label>

            <label className={classes.CheckboxLabel}>
              <input
                type="checkbox"
                checked={filterOyOnly}
                onChange={(e) => setFilterOyOnly(e.target.checked)}
              />
              <span>Osakeyhtiöt (Oy)</span>
              <span className={classes.CheckboxBadgeCount}>{counts.oyOnly}</span>
            </label>

            <label className={classes.CheckboxLabel}>
              <input
                type="checkbox"
                checked={filterTmiOnly}
                onChange={(e) => setFilterTmiOnly(e.target.checked)}
              />
              <span>Toiminimet (Tmi)</span>
              <span className={classes.CheckboxBadgeCount}>{counts.tmiOnly}</span>
            </label>
          </div>

          {/* Action tools: Service select, Cadence select & BCC copy button */}
          <div className={classes.FilterActionsGroup}>
            <select
              className={classes.ServiceFilterSelect}
              value={serviceFilter}
              onChange={(e) => {
                setServiceFilter(e.target.value);
                if (!selectedServiceToken && e.target.value) {
                  setSelectedServiceToken(e.target.value);
                }
              }}
              title="Suodata palvelun mukaan"
            >
              <option value="">Kaikki palvelut</option>
              {(services || []).filter(s => !s.archived && s.serviceToken).map((svc) => (
                <option key={svc.id} value={svc.serviceToken}>{svc.name || svc.serviceToken}</option>
              ))}
            </select>

            <button
              type="button"
              className={`${classes.BccCopyBtn} ${copiedBcc ? classes.BccCopySuccess : ""}`}
              onClick={handleCopyBcc}
              disabled={bccEmails.length === 0}
              title="Kopioi valittujen/suodatettujen yritysten sähköpostit Gmailin piilokopiota (BCC) varten"
            >
              <FontAwesomeIcon icon={copiedBcc ? faCheck : faCopy} />
              {copiedBcc ? `Kopioitu (${bccEmails.length})!` : bccButtonLabel}
            </button>
          </div>
        </div>
      </div>

      {error && <p style={{ color: "#dc2626" }}>{error}</p>}

      {/* TABLE VIEW */}
      {viewMode === "table" && (
        <>
          {filteredLeads.length === 0 ? (
            <div className={classes.EmptyState}>Ei liidejä{statusFilter !== "ALL" ? " tällä statuksella" : ""}</div>
          ) : (
            <table className={classes.Table}>
              <thead>
                <tr>
                  <th style={{ width: "40px", textAlign: "center" }}>
                    <input
                      type="checkbox"
                      checked={filteredLeads.length > 0 && selectedLeadIds.length === filteredLeads.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedLeadIds(filteredLeads.map(l => l.id));
                        } else {
                          setSelectedLeadIds([]);
                        }
                      }}
                    />
                  </th>
                  <th>Yritys</th>
                  <th>Y-tunnus</th>
                  <th>Kaupunki</th>
                  <th>Toimiala</th>
                  <th>Status</th>
                  <th>Lisätty palveluntarjoajaksi</th>
                  <th>Kontaktointi</th>
                  <th>Luotu</th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.map((lead, index) => {
                  const isSelected = selectedLeadIds.includes(lead.id);
                  return (
                    <tr
                      key={lead.id}
                      className={isSelected ? classes.RowSelected : ""}
                      onClick={(e) => {
                        if (e.shiftKey) {
                          e.preventDefault();
                          handleToggleLeadSelection(lead.id, index, true);
                        } else if (e.ctrlKey || e.metaKey) {
                          e.preventDefault();
                          handleToggleLeadSelection(lead.id, index, false);
                        } else {
                          setLastSelectedIndex(index);
                          openDrawer(lead);
                        }
                      }}
                    >
                      <td
                        style={{ textAlign: "center", cursor: "pointer" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleLeadSelection(lead.id, index, e.shiftKey);
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          readOnly
                          style={{ pointerEvents: "none", cursor: "pointer" }}
                        />
                      </td>
                    <td>
                      <strong>{lead.ytjName || lead.placeName || "-"}</strong>
                      {lead.isToiminimi && (
                        <span className={classes.ToiminimiBadge} style={{ marginLeft: "0.5rem" }}>
                          Tmi
                        </span>
                      )}
                      {lead.source && (
                        <span className={`${classes.SourceBadge} ${lead.source === 'OUTREACH_BATCH' ? classes.SourceBatch : classes.SourceManual}`}>
                          {lead.source === 'OUTREACH_BATCH' ? 'Taustahaku' : 'Manuaalinen'}
                        </span>
                      )}
                    </td>
                    <td>{lead.businessId || "-"}</td>
                    <td>{lead.city || "-"}</td>
                    <td>{lead.businessLine || "-"}</td>
                    <td>
                      <span className={`${classes.StatusBadge} ${statusBadgeClass(lead.status)}`}>
                        {LEAD_STATUS_LABELS[lead.status] || lead.status}
                      </span>
                    </td>
                    <td>
                      {lead.convertedToProvider ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.2rem" }}>
                          <span style={{ color: "#16a34a", fontWeight: 500 }}>
                            <FontAwesomeIcon icon={faCheck} style={{ marginRight: "0.3rem" }} />
                            {lead.convertedToProviderDate ? formatDate(lead.convertedToProviderDate) : "Kyllä"}
                          </span>
                          {lead.linkedOrgId && (
                            <a
                              href={Routes.SERVICE_PROVIDER(String(lead.linkedOrgId))}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={classes.TableProfileLink}
                              onClick={(e) => e.stopPropagation()}
                              title="Avaa palveluntarjoajan profiili uudessa välilehdessä"
                            >
                              Avaa profiili <FontAwesomeIcon icon={faExternalLinkAlt} style={{ fontSize: "0.72rem" }} />
                            </a>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: "#94a3b8" }}>-</span>
                      )}
                    </td>
                    <td>
                      {lead.status === LeadStatus.CONTACTED ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                          {renderContactStepBoxes(lead)}
                          <span style={{ fontSize: "0.72rem", color: "#64748b" }}>
                            {(lead.salesContactCount ?? lead.contactCount) ? `${(lead.salesContactCount ?? lead.contactCount)}/3 viestiä` : "0/3 viestiä"}
                            {lead.contactMethod && ` · ${CONTACT_METHOD_LABELS[lead.contactMethod] || lead.contactMethod}`}
                          </span>
                        </div>
                      ) : (
                        <span style={{ color: "#94a3b8" }}>-</span>
                      )}
                    </td>
                    <td>{formatDateTime(lead.created)}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </>
      )}

      {/* PIPELINE VIEW */}
      {viewMode === "pipeline" && (
        <div className={classes.Board}>
          {pipelineColumns
            .filter((col) => statusFilter === "ALL" || statusFilter === col.status)
            .map((col) => (
            <div
              className={classes.Column}
              key={col.status}
              onDragOver={(e) => e.preventDefault()}
              onDrop={async (e) => {
                e.preventDefault();
                const leadId = e.dataTransfer.getData("text/plain");
                if (!leadId) return;
                const lead = leads.find((l) => l.id === leadId);
                if (!lead || lead.status === col.status) return;
                const previousStatus = lead.status;
                // Optimistic UI update
                setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, status: col.status } : l)));
                try {
                  const updated = await LeadService.updateLead(leadId, { status: col.status });
                  setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, ...updated } : l)));
                } catch (err) {
                  console.error("DnD status update failed:", err);
                  // Rollback
                  setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, status: previousStatus } : l)));
                  setError("Virhe tilan päivityksessä raahauksen jälkeen.");
                }
                setDraggedLeadId(null);
              }}
            >
              <div className={classes.ColHeader}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <h3 style={{ color: col.color, margin: 0 }}>{col.label}</h3>
                  {col.status === LeadStatus.CONTACTED && (
                    <select
                      className={classes.PipelineContactSelect}
                      value={pipelineContactFilter}
                      onChange={(e) => setPipelineContactFilter(e.target.value as any)}
                      onClick={(e) => e.stopPropagation()}
                      title="Suodata Kontaktoitu-sarakkeen sekvenssivaiheen mukaan"
                    >
                      <option value="ALL">Kaikki</option>
                      <option value="0">0/3</option>
                      <option value="1">1/3</option>
                      <option value="2">2/3</option>
                      <option value="3">3/3</option>
                    </select>
                  )}
                </div>
                <span className={classes.CountBadge}>{leadsByStatus[col.status]?.length || 0}</span>
              </div>
              <div className={classes.CardList}>
                {(leadsByStatus[col.status] || []).length === 0 ? (
                  <div className={classes.EmptyColumn}>Ei liidejä</div>
                ) : (
                  (leadsByStatus[col.status] || []).map((lead) => {
                    const isSelected = selectedLeadIds.includes(lead.id);
                    const flatIdx = filteredLeads.findIndex((l) => l.id === lead.id);
                    return (
                      <div
                        className={`${classes.Card} ${isSelected ? classes.CardSelected : ""} ${draggedLeadId === lead.id ? classes.CardDragging : ""} ${searchQuery.trim() && !isSearchMatch(lead) ? classes.CardDimmed : ""} ${searchQuery.trim() && isSearchMatch(lead) ? classes.CardHighlighted : ""}`}
                        key={lead.id}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData("text/plain", lead.id);
                          setDraggedLeadId(lead.id);
                        }}
                        onDragEnd={() => setDraggedLeadId(null)}
                        onClick={(e) => {
                          if (e.shiftKey) {
                            e.preventDefault();
                            handleToggleLeadSelection(lead.id, flatIdx, true);
                          } else if (e.ctrlKey || e.metaKey) {
                            e.preventDefault();
                            handleToggleLeadSelection(lead.id, flatIdx, false);
                          } else {
                            setLastSelectedIndex(flatIdx);
                            openDrawer(lead);
                          }
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem" }}>
                          <div className={classes.CardName}>{lead.ytjName || lead.placeName || "-"}</div>
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleLeadSelection(lead.id, flatIdx, e.shiftKey);
                            }}
                            style={{ cursor: "pointer", display: "inline-flex" }}
                            title="Valitse liidi (Shift-klikkaus valitsee välin)"
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              readOnly
                              style={{ pointerEvents: "none", cursor: "pointer", marginTop: "2px" }}
                            />
                          </div>
                        </div>
                        <div className={classes.CardMeta}>
                          {lead.city && <span>{lead.city}</span>}
                          {lead.businessLine && <span>{lead.businessLine}</span>}
                        </div>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
                          {lead.isToiminimi && (
                            <span className={classes.ToiminimiBadge} style={{ width: "fit-content" }}>
                              Tmi
                            </span>
                          )}
                          {lead.source && (
                            <span className={`${classes.SourceBadge} ${lead.source === 'OUTREACH_BATCH' ? classes.SourceBatch : classes.SourceManual}`} style={{ width: 'fit-content', marginLeft: 0 }}>
                              {lead.source === 'OUTREACH_BATCH' ? 'Taustahaku' : 'Manuaalinen'}
                            </span>
                          )}
                        </div>
                        {col.status === LeadStatus.CONTACTED && (
                          <div style={{ marginTop: "0.4rem", paddingTop: "0.4rem", borderTop: "1px dashed #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            {renderContactStepBoxes(lead)}
                            <span style={{ fontSize: "0.72rem", color: "#64748b", fontWeight: 600 }}>
                              {(lead.salesContactCount ?? lead.contactCount) ? `${(lead.salesContactCount ?? lead.contactCount)}/3` : "0/3"}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* DRAWER */}
      {drawerOpen && selectedLead && (
        <div className={classes.DrawerOverlay} onClick={closeDrawer}>
          <div className={classes.Drawer} onClick={(e) => e.stopPropagation()}>
            <div className={classes.DrawerHeader}>
              <div>
                <h4>{selectedLead.ytjName || selectedLead.placeName || "Liidi"}</h4>
                <div className={classes.DrawerSub}>
                  {selectedLead.businessId && <span>Y-tunnus: {selectedLead.businessId}</span>}
                  {selectedLead.companyForm && <span> · {selectedLead.companyForm}</span>}
                  {selectedLead.source && (
                    <span className={`${classes.SourceBadge} ${selectedLead.source === 'OUTREACH_BATCH' ? classes.SourceBatch : classes.SourceManual}`}>
                      {selectedLead.source === 'OUTREACH_BATCH' ? 'Taustahaku' : 'Manuaalinen'}
                    </span>
                  )}
                  {selectedLead.archived && (
                    <span style={{ marginLeft: "0.5rem", background: "#fee2e2", color: "#b91c1c", padding: "1px 6px", borderRadius: "4px", fontSize: "0.7rem", fontWeight: 600 }}>
                      Arkistoitu
                    </span>
                  )}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <button
                  type="button"
                  className={classes.DrawerArchiveBtn}
                  onClick={() => handleToggleArchiveLead(selectedLead)}
                  title={selectedLead.archived ? "Palauta liidi aktiivisiin" : "Arkistoi liidi"}
                >
                  <FontAwesomeIcon icon={selectedLead.archived ? faUndo : faArchive} />
                  <span>{selectedLead.archived ? "Palauta aktiiviseksi" : "Arkistoi"}</span>
                </button>
                <button className={classes.CloseBtn} onClick={closeDrawer}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            </div>

            <div className={classes.DrawerScroll}>
              {/* Archive banner (Vaihtoehto 3) */}
              {selectedLead.archived && (
                <div className={classes.DrawerArchiveBanner}>
                  <FontAwesomeIcon icon={faArchive} />
                  <span>
                    Tämä yritys on arkistoitu (tila mihin jäi: <strong>{LEAD_STATUS_LABELS[selectedLead.status] || selectedLead.status}</strong>). Muistiinpanot ja historia ovat tallessa.
                  </span>
                </div>
              )}

              {/* 3-vaiheinen Kontaktointi (Hoitosekvenssi) */}
              <div className={classes.SectionBox}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <h5 style={{ margin: 0 }}>3-vaiheinen kontaktointi</h5>
                  <span className={classes.ContactStepBadge}>
                    {((selectedLead.salesContactCount ?? selectedLead.contactCount) || 0)}/3 viestiä lähetetty
                  </span>
                </div>
                <p className={classes.ContactStepHelp}>
                  Merkitse sähköpostisekvenssin vaiheet lähetetyksi neliöistä:
                </p>
                <div className={classes.DrawerContactSteps}>
                  {[
                    { step: 1, title: "1. Viesti: Arvolupaus", desc: "Yritys lisätty kartalle – tarkista tiedot" },
                    { step: 2, title: "2. Viesti: Kysyntä & Todiste", desc: "Uusia tarjouspyyntöjä alueella – aktivoi vastaanotto" },
                    { step: 3, title: "3. Viesti: Viimeinen karsinta", desc: "Haluatteko säilyttää näkyvyytenne?" },
                  ].map(({ step, title, desc }) => {
                    const count = (selectedLead.salesContactCount ?? selectedLead.contactCount) || 0;
                    const isChecked = count >= step;
                    return (
                      <div
                        key={step}
                        className={`${classes.DrawerStepRow} ${isChecked ? classes.DrawerStepRowChecked : ""}`}
                        onClick={() => {
                          const nextCount = isChecked && count === step ? step - 1 : step;
                          handleUpdateContactCount(selectedLead, nextCount);
                        }}
                      >
                        <button
                          type="button"
                          className={`${classes.ContactStepBox} ${classes.ContactStepBoxDrawer} ${isChecked ? classes.ContactStepBoxChecked : ""}`}
                        >
                          {isChecked ? "✓" : step}
                        </button>
                        <div className={classes.DrawerStepInfo}>
                          <div className={classes.DrawerStepTitle}>{title}</div>
                          <div className={classes.DrawerStepDesc}>{desc}</div>
                        </div>
                        <span className={classes.DrawerStepStatusText}>
                          {isChecked ? "Lähetetty" : "Ei lähetetty"}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* Converted to provider banner (A1) */}
              {selectedLead.convertedToProvider && selectedLead.linkedOrgId && (
                <div className={classes.DrawerProviderBanner}>
                  <div className={classes.ProviderBannerBadge}>
                    <FontAwesomeIcon icon={faCheck} />
                    <span>Lisätty palveluntarjoajaksi {selectedLead.convertedToProviderDate ? `(${formatDate(selectedLead.convertedToProviderDate)})` : ""}</span>
                  </div>
                  <a
                    href={Routes.SERVICE_PROVIDER(String(selectedLead.linkedOrgId))}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.DrawerProfileLinkBtn}
                  >
                    <FontAwesomeIcon icon={faExternalLinkAlt} />
                    Avaa palveluntarjoajan profiili
                  </a>
                </div>
              )}

              {/* Company info */}
              <div className={classes.SectionBox}>
                <h5>Yrityksen tiedot</h5>
                <dl className={classes.DetailGrid}>
                  {selectedLead.ytjName && <><dt>YTJ-nimi</dt><dd>{selectedLead.ytjName}</dd></>}
                  {selectedLead.businessId && <><dt>Y-tunnus</dt><dd>{selectedLead.businessId}</dd></>}
                  {selectedLead.companyForm && <><dt>Yhtiömuoto</dt><dd>{selectedLead.companyForm}</dd></>}
                  {selectedLead.businessLine && <><dt>Toimiala</dt><dd>{selectedLead.businessLine}</dd></>}
                  {selectedLead.street && <><dt>Osoite</dt><dd>{selectedLead.street}, {selectedLead.postCode} {selectedLead.city}</dd></>}
                  {selectedLead.contactPerson && (
                    <>
                      <dt>Yhteyshenkilö</dt>
                      <dd style={{ fontWeight: 600, color: "#0f172a" }}>
                        <FontAwesomeIcon icon={faUserTie} style={{ marginRight: "0.4rem", color: "#64748b" }} />
                        {selectedLead.contactPerson}
                      </dd>
                    </>
                  )}
                  {selectedLead.phone && <><dt>Puhelin</dt><dd>{selectedLead.phone}</dd></>}
                  {selectedLead.email && (
                    <>
                      <dt>Sähköposti</dt>
                      <dd style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                        <a href={`mailto:${selectedLead.email}`} style={{ color: "#2563eb", textDecoration: "none" }}>
                          {selectedLead.email}
                        </a>
                        <button
                          type="button"
                          className={classes.InlineCopyBtn}
                          onClick={() => {
                            navigator.clipboard.writeText(selectedLead.email!);
                            setCopiedDrawerEmail(true);
                            setTimeout(() => setCopiedDrawerEmail(false), 2000);
                          }}
                          title="Kopioi sähköposti leikepöydälle"
                        >
                          <FontAwesomeIcon icon={copiedDrawerEmail ? faCheck : faCopy} />
                          {copiedDrawerEmail ? "Kopioitu!" : "Kopioi"}
                        </button>
                      </dd>
                    </>
                  )}
                  {selectedLead.website && <><dt>Verkkosivut</dt><dd><a href={selectedLead.website.startsWith("http") ? selectedLead.website : `https://${selectedLead.website}`} target="_blank" rel="noopener noreferrer">{selectedLead.website}</a></dd></>}
                  {selectedLead.description && (
                    <>
                      <dt>AI-kuvaus</dt>
                      <dd style={{ fontStyle: 'italic', color: '#475569', lineHeight: 1.5 }}>{selectedLead.description}</dd>
                    </>
                  )}
                </dl>
                {selectedLead.placeId && (
                  <div style={{ marginTop: "0.75rem" }}>
                    <a
                      href={`https://www.google.com/maps/place/?q=place_id:${selectedLead.placeId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: "0.85rem", color: "#2563eb" }}
                    >
                      <FontAwesomeIcon icon={faExternalLinkAlt} /> Avaa Google Mapsissa
                    </a>
                  </div>
                )}
              </div>

              {/* Status */}
              <div className={classes.SectionBox}>
                <h5>Status{updatingStatus && <FontAwesomeIcon icon={faSpinner} spin style={{ marginLeft: "0.5rem", fontSize: "0.8rem" }} />}</h5>
                <div className={classes.StateSelectorRow}>
                  {Object.entries(LEAD_STATUS_LABELS).map(([key, label]) => (
                    <button
                      key={key}
                      className={`${classes.StateBtn} ${selectedLead.status === key ? classes.StateBtnActive : ""}`}
                      onClick={() => handleStatusChange(key as LeadStatus)}
                      disabled={updatingStatus}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Muistiinpanot ja Aktiviteetit */}
              <div className={classes.SectionBox}>
                <h5>
                  <FontAwesomeIcon icon={faClock} style={{ marginRight: "0.5rem", color: "#2563eb" }} />
                  Muistiinpanot & Aktiviteetit {timelineItems.length > 0 && <span style={{ color: "#64748b", fontWeight: "normal", fontSize: "0.85rem" }}>({timelineItems.length})</span>}
                </h5>

                {/* New Note Composer */}
                <div className={classes.ActivityComposer} style={{ marginTop: "0.5rem" }}>
                  <div className={classes.ComposerHeader}>
                    <span className={classes.ComposerTitle}>
                      <FontAwesomeIcon icon={faCommentAlt} style={{ marginRight: "0.4rem", color: "#2563eb" }} />
                      Kirjaa uusi muistiinpano
                    </span>
                    {selectedLead.serviceToken && (
                      <span className={classes.ComposerServiceTag}>
                        Palvelu: {services?.find((s) => s.serviceToken === selectedLead.serviceToken)?.name || selectedLead.serviceToken}
                      </span>
                    )}
                  </div>
                  <textarea
                    className={classes.ComposerTextarea}
                    placeholder="Kirjoita muistiinpano... (Paina Enter tallentaaksesi, Shift+Enter rivinvaihtoon)"
                    value={newNote}
                    rows={2}
                    onChange={(e) => setNewNote(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleAddNote();
                      }
                    }}
                    disabled={savingNote}
                  />
                  <div className={classes.ComposerFooter}>
                    <span className={classes.ComposerHint}>Enter tallentaa · Shift+Enter rivinvaihto</span>
                    <Button
                      onClick={handleAddNote}
                      disabled={savingNote || !newNote.trim()}
                      style={{ padding: "0.35rem 1rem", fontSize: "0.85rem" }}
                    >
                      {savingNote ? <FontAwesomeIcon icon={faSpinner} spin /> : "Lisää muistiinpano"}
                    </Button>
                  </div>
                </div>

                {/* Filter Pills */}
                <div className={classes.ActivityFilterRow} style={{ marginTop: "0.75rem" }}>
                  <button
                    className={`${classes.ActivityFilterBtn} ${activityFilter === "ALL" ? classes.ActivityFilterBtnActive : ""}`}
                    onClick={() => setActivityFilter("ALL")}
                  >
                    Kaikki ({timelineItems.length})
                  </button>
                  <button
                    className={`${classes.ActivityFilterBtn} ${activityFilter === "NOTES" ? classes.ActivityFilterBtnActive : ""}`}
                    onClick={() => setActivityFilter("NOTES")}
                  >
                    Muistiinpanot ({notes.length})
                  </button>
                  <button
                    className={`${classes.ActivityFilterBtn} ${activityFilter === "CHANGES" ? classes.ActivityFilterBtnActive : ""}`}
                    onClick={() => setActivityFilter("CHANGES")}
                  >
                    Muutokset & Lokit ({timelineItems.length - notes.length})
                  </button>
                </div>

                {/* Timeline */}
                {loadingNotes ? (
                  <div style={{ textAlign: "center", padding: "2rem" }}><Spinner /></div>
                ) : filteredTimelineItems.length === 0 ? (
                  <div className={classes.EmptyTimeline}>
                    Ei näytettäviä tapahtumia valitulla suodatuksella.
                  </div>
                ) : (
                  <div className={classes.Timeline}>
                    {filteredTimelineItems.map((item) => (
                      <div className={classes.TimelineItem} key={item.id}>
                        {/* Left Icon Badge */}
                        <div
                          className={`${classes.TimelineIconTrack} ${
                            item.type === "NOTE"
                              ? classes.TimelineIconNote
                              : item.type === "STATUS_CHANGE"
                              ? classes.TimelineIconStatus
                              : item.type === "EMAIL_SENT"
                              ? classes.TimelineIconEmail
                              : item.type === "CONVERTED"
                              ? classes.TimelineIconConvert
                              : item.type === "CREATED"
                              ? classes.TimelineIconCreate
                              : classes.TimelineIconNote
                          }`}
                        >
                          {item.type === "NOTE" && <FontAwesomeIcon icon={faCommentAlt} />}
                          {item.type === "STATUS_CHANGE" && <FontAwesomeIcon icon={faSyncAlt} />}
                          {item.type === "EMAIL_SENT" && <FontAwesomeIcon icon={faPaperPlane} />}
                          {item.type === "CONVERTED" && <FontAwesomeIcon icon={faUserPlus} />}
                          {item.type === "CREATED" && <FontAwesomeIcon icon={faPlusCircle} />}
                          {(item.type === "UPDATE" || item.type === "SYSTEM") && <FontAwesomeIcon icon={faInfoCircle} />}
                        </div>

                        {/* Event Card */}
                        <div className={classes.TimelineCard}>
                          <div className={classes.TimelineHeader}>
                            <div className={classes.TimelineAuthorMeta}>
                              {item.type === "NOTE" && (
                                <div className={classes.AvatarCircle}>
                                  {getInitials(item.authorName)}
                                </div>
                              )}
                              <span className={classes.TimelineAuthorName}>{item.authorName || item.title}</span>
                              <span>·</span>
                              <span className={classes.TimelineDate} title={formatDateTime(item.timestamp)}>
                                {timeAgo(item.timestamp)}
                              </span>
                              {item.serviceName && (
                                <span className={classes.ServiceBadge}>{item.serviceName}</span>
                              )}
                            </div>

                            {item.type === "NOTE" && item.noteId && (
                              <div className={classes.TimelineActions}>
                                <button
                                  className={classes.ItemEditBtn}
                                  title="Muokkaa muistiinpanoa"
                                  onClick={() => handleStartEditNote(item.rawNote!)}
                                >
                                  <FontAwesomeIcon icon={faPencilAlt} />
                                </button>
                                <button
                                  className={classes.ItemDeleteBtn}
                                  title="Poista muistiinpano"
                                  onClick={() => handleDeleteNote(item.noteId!)}
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              </div>
                            )}
                          </div>

                          {/* Note inline editing mode */}
                          {item.type === "NOTE" && editingNoteId === item.noteId ? (
                            <div className={classes.EditNoteBox}>
                              <textarea
                                value={editingNoteContent}
                                onChange={(e) => setEditingNoteContent(e.target.value)}
                                rows={2}
                                disabled={savingEditNote}
                              />
                              <div className={classes.EditActions}>
                                <button
                                  onClick={handleCancelEditNote}
                                  style={{
                                    background: "none",
                                    border: "1px solid #cbd5e1",
                                    padding: "0.2rem 0.6rem",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    fontSize: "0.78rem",
                                  }}
                                >
                                  Peruuta
                                </button>
                                <Button
                                  onClick={() => handleSaveEditNote(item.noteId!)}
                                  disabled={savingEditNote || !editingNoteContent.trim()}
                                  style={{ padding: "0.2rem 0.6rem", fontSize: "0.78rem" }}
                                >
                                  {savingEditNote ? <FontAwesomeIcon icon={faSpinner} spin /> : "Tallenna"}
                                </Button>
                              </div>
                            </div>
                          ) : item.type === "STATUS_CHANGE" ? (
                            <div className={classes.StatusChangeFlow}>
                              <span className={classes.StatusOldBadge}>
                                {item.oldLabel || item.oldValue || "Ei tilaa"}
                              </span>
                              <span className={classes.StatusArrow}>➔</span>
                              <span className={classes.StatusNewBadge}>
                                {item.newLabel || item.newValue}
                              </span>
                            </div>
                          ) : (
                            <div className={classes.TimelineContent}>
                              {item.description || item.title}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Email */}
              {(selectedLead.generatedEmailSubject || selectedLead.generatedEmailBody) && (
                <div className={classes.SectionBox}>
                  <h5>
                    <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: "0.5rem" }} />
                    Generoitu sähköposti
                  </h5>
                  <div className={classes.EmailBox}>
                    <div className={classes.EmailSubject}>{selectedLead.generatedEmailSubject || "-"}</div>
                    <div className={classes.EmailBody}>{selectedLead.generatedEmailBody || "-"}</div>
                  </div>
                  <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem", flexWrap: "wrap" }}>
                    <Button onClick={handleCopyEmail}>
                      <FontAwesomeIcon icon={faCopy} /> Kopioi leikepöydälle
                    </Button>
                    {!selectedLead.isToiminimi && selectedLead.email && (
                      <Button onClick={handleSendEmail} disabled={sendingEmail}>
                        {sendingEmail ? (
                          <><FontAwesomeIcon icon={faSpinner} spin /> Lähetetään...</>
                        ) : (
                          <><FontAwesomeIcon icon={faPaperPlane} /> Lähetä MailGunilla</>
                        )}
                      </Button>
                    )}
                  </div>
                  {selectedLead.emailSentAt && (
                    <div className={classes.EmailSentBadge}>
                      <FontAwesomeIcon icon={faCheck} />
                      Lähetetty {formatDateTime(selectedLead.emailSentAt)}
                    </div>
                  )}
                  {emailSent && !selectedLead.emailSentAt && (
                    <div className={classes.EmailSentBadge}>
                      <FontAwesomeIcon icon={faCheck} />
                      Sähköposti lähetetty!
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className={classes.DrawerFooter}>
              <button className={classes.DeleteBtn} onClick={handleDelete} disabled={deletingLead}>
                <FontAwesomeIcon icon={faTrash} /> {deletingLead ? "Poistetaan..." : "Poista liidi"}
              </button>
              <Button onClick={closeDrawer}>Sulje</Button>
            </div>
          </div>
        </div>
      )}
      {/* CONFIRM DIALOG */}
      {showConfirm && (
        <div className={classes.DrawerOverlay} onClick={() => setShowConfirm(false)}>
          <div style={{ background: "white", borderRadius: "12px", padding: "2rem", maxWidth: "480px", margin: "10vh auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Vahvista palveluntarjoajiksi vienti</h3>
            <p>Olet lisäämässä <strong>{selectedLeadIds.length} liidiä</strong> palveluntarjoajiksi.</p>
            <p style={{ color: "#475569", fontSize: "0.9rem", lineHeight: 1.5 }}>
              Yritykset säilyvät Liidit-työpöydällä ja saavat profiililinkin. Muistiinpanot siirtyvät ja synkronoituvat automaattisesti palveluntarjoajan ja liidin välillä.
            </p>
            <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end", marginTop: "1.5rem" }}>
              <button onClick={() => setShowConfirm(false)} style={{ padding: "0.5rem 1rem", border: "1px solid #d1d5db", borderRadius: "6px", background: "white", cursor: "pointer" }}>Peruuta</button>
              <Button onClick={async () => {
                setShowConfirm(false);
                setConverting(true);
                try {
                  const result = await LeadService.bulkConvertToProviders(selectedServiceToken, selectedLeadIds);
                  setConvertResult(result);
                  setSelectedLeadIds([]);
                  setFilterNewOnly(false); // Make sure converted leads remain visible in the view
                  await fetchLeads(); // Refresh leads to get convertedToProvider flags and linkedOrgIds
                } catch (err: any) {
                  setError("Konversio epäonnistui: " + err.message);
                } finally {
                  setConverting(false);
                }
              }}>
                <FontAwesomeIcon icon={faUserPlus} style={{ marginRight: "0.4rem" }} />
                Lisää palveluntarjoajiksi
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRM DIALOG */}
      {showDeleteConfirm && (
        <div className={classes.DrawerOverlay} onClick={() => !deleting && setShowDeleteConfirm(false)}>
          <div
            style={{
              background: "white",
              borderRadius: "12px",
              padding: "2rem",
              maxWidth: "460px",
              margin: "12vh auto",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, fontSize: "1.35rem", color: "#0f172a", marginBottom: "0.75rem" }}>
              Poista?
            </h3>
            <p style={{ color: "#334155", fontSize: "0.95rem", lineHeight: 1.5, margin: "0 0 0.5rem 0" }}>
              Haluatko varmasti poistaa valitut <strong>{selectedLeadIds.length} liidiä</strong> näkymästä?
            </p>
            <p style={{ color: "#64748b", fontSize: "0.85rem", margin: "0 0 1.5rem 0", lineHeight: 1.4 }}>
              Toiminto poistaa liidit Liidit-työpöydältä. Palveluntarjoajaprofiileja ei poisteta.
            </p>
            <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end", alignItems: "center" }}>
              <button
                type="button"
                disabled={deleting}
                onClick={() => setShowDeleteConfirm(false)}
                style={{
                  padding: "0.6rem 1.4rem",
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  background: "#f1f5f9",
                  color: "#475569",
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  cursor: deleting ? "not-allowed" : "pointer",
                }}
              >
                Ei
              </button>
              <button
                type="button"
                disabled={deleting}
                onClick={async () => {
                  setDeleting(true);
                  try {
                    await LeadService.bulkDeleteLeads(selectedLeadIds);
                    setSelectedLeadIds([]);
                    setShowDeleteConfirm(false);
                    await fetchLeads();
                  } catch (err: any) {
                    setError("Liidien poisto epäonnistui: " + (err.message || err));
                  } finally {
                    setDeleting(false);
                  }
                }}
                style={{
                  padding: "0.6rem 1.4rem",
                  border: "none",
                  borderRadius: "6px",
                  background: "#ff6b00",
                  color: "#ffffff",
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  cursor: deleting ? "not-allowed" : "pointer",
                  boxShadow: "0 2px 6px rgba(255, 107, 0, 0.35)",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                {deleting && <FontAwesomeIcon icon={faSpinner} spin />}
                Kyllä
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CONVERT RESULT MODAL */}
      {convertResult && (
        <div className={classes.DrawerOverlay} onClick={() => setConvertResult(null)}>
          <div style={{ background: "white", borderRadius: "12px", padding: "2rem", maxWidth: "600px", margin: "10vh auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)", maxHeight: "70vh", overflow: "auto" }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Konversion tulos</h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "1rem", marginBottom: "1.5rem" }}>
              <div style={{ background: "#f0fdf4", padding: "1rem", borderRadius: "8px", textAlign: "center" }}>
                <div style={{ fontSize: "2rem", fontWeight: 700, color: "#16a34a" }}>{convertResult.created}</div>
                <div style={{ color: "#16a34a", fontSize: "0.85rem" }}>Luotu</div>
              </div>
              <div style={{ background: "#fefce8", padding: "1rem", borderRadius: "8px", textAlign: "center" }}>
                <div style={{ fontSize: "2rem", fontWeight: 700, color: "#ca8a04" }}>{convertResult.skipped + convertResult.alreadyConverted}</div>
                <div style={{ color: "#ca8a04", fontSize: "0.85rem" }}>Ohitettu</div>
              </div>
              {convertResult.errors > 0 && (
                <div style={{ background: "#fef2f2", padding: "1rem", borderRadius: "8px", textAlign: "center" }}>
                  <div style={{ fontSize: "2rem", fontWeight: 700, color: "#dc2626" }}>{convertResult.errors}</div>
                  <div style={{ color: "#dc2626", fontSize: "0.85rem" }}>Virhe</div>
                </div>
              )}
            </div>
            {convertResult.details && convertResult.details.length > 0 && (
              <details>
                <summary style={{ cursor: "pointer", color: "#64748b", marginBottom: "0.5rem" }}>Näytä yksityiskohdat ({convertResult.details.length})</summary>
                <div style={{ maxHeight: "300px", overflow: "auto", fontSize: "0.85rem" }}>
                  {convertResult.details.map((d, i) => (
                    <div key={i} style={{ padding: "0.25rem 0", borderBottom: "1px solid #f1f5f9" }}>
                      <span style={{ color: d.status === 'CREATED' ? '#16a34a' : d.status === 'ERROR' ? '#dc2626' : '#ca8a04' }}>
                        {d.status === 'CREATED' ? '✅' : d.status === 'ERROR' ? '❌' : '⏭️'}
                      </span>
                      {' '}{d.name || d.leadId} {d.reason ? `— ${d.reason}` : ''}
                    </div>
                  ))}
                </div>
              </details>
            )}
            <div style={{ textAlign: "right", marginTop: "1rem" }}>
              <Button onClick={() => setConvertResult(null)}>Sulje</Button>
            </div>
          </div>
        </div>
      )}

      {/* CONVERTING OVERLAY */}
      {converting && (
        <div className={classes.DrawerOverlay} style={{ zIndex: 1100 }}>
          <div style={{ background: "white", borderRadius: "12px", padding: "3rem", maxWidth: "400px", margin: "20vh auto", textAlign: "center", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: "2rem", color: "#2563eb", marginBottom: "1rem" }} />
            <p style={{ fontSize: "1.1rem", fontWeight: 500 }}>Lisätään palveluntarjoajia...</p>
            <p style={{ color: "#64748b", fontSize: "0.9rem" }}>Tämä voi kestää hetken.</p>
          </div>
        </div>
      )}
    </Container>
  );
};

export default Leads;
